import { useState } from "react";
import { useSim } from "../state/store";
import type { RiskMeterItem } from "../state/store";

/**
 * Corner Risk Meter HUD for remote-work-style courses. Driven by weighted
 * scoringEvents (risk adds, positive subtracts). Caps at 100; never auto-ends.
 *
 * 2026-07-15: expandable into a "learning card" -- clicking the head opens a
 * running, spoiler-free log of every decision scored SO FAR (newest first),
 * each tagged risk/positive with its weight, and each individually
 * expandable to a 1-2 sentence "why this matters" explanation. Nothing about
 * choices not yet made is ever shown -- the list only grows as the learner
 * actually plays through the scenario.
 */
/**
 * Shared "decision log" body -- the list of scored items with per-row
 * expandable detail. Used by BOTH the corner HUD (below) and the end-of-run
 * Debrief results screen (`ui/Debrief.tsx`), so a learner who wants to
 * review every choice Max made has one consistent place to do it, framed as
 * an optional reference card rather than a verdict.
 */
export function RiskMeterList({ items }: { items: RiskMeterItem[] }) {
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  if (items.length === 0) {
    return (
      <p className="risk-meter-empty">
        Nothing scored yet — keep going. Every choice Max makes will show up here as a risk or a
        good call, with the reason why.
      </p>
    );
  }
  return (
    <ul className="risk-meter-items">
      {items.map((it) => (
        <RiskMeterRow
          key={it.id}
          item={it}
          open={openItemId === it.id}
          onToggle={() => setOpenItemId((cur) => (cur === it.id ? null : it.id))}
        />
      ))}
    </ul>
  );
}

export function RiskMeter() {
  const meter = useSim((s) => s.riskMeter());
  const [expanded, setExpanded] = useState(false);

  if (!meter.enabled) return null;

  const band =
    meter.percent <= 25
      ? "controlled"
      : meter.percent <= 50
      ? "risky"
      : meter.percent <= 75
      ? "serious"
      : "incident";

  const label =
    band === "controlled"
      ? "Controlled"
      : band === "risky"
      ? "Risky"
      : band === "serious"
      ? "Serious"
      : "Incident";

  return (
    <div
      className={`risk-meter risk-meter--${band} ${expanded ? "risk-meter--open" : ""}`}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="risk-meter-head"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse risk meter details" : "Expand risk meter details"}
      >
        <span className="risk-meter-kicker">Risk meter</span>
        <span className="risk-meter-head-right">
          <span className="risk-meter-pct">{meter.percent}%</span>
          <span className="risk-meter-chevron" aria-hidden>
            {expanded ? "\u25B4" : "\u25BE"}
          </span>
        </span>
      </button>
      <div className="risk-meter-track" aria-hidden>
        <div className="risk-meter-fill" style={{ width: `${meter.percent}%` }} />
      </div>
      <div className="risk-meter-band">{label}</div>

      {expanded && (
        <div className="risk-meter-card">
          <div className="risk-meter-card-head">
            Decisions so far <span className="risk-meter-card-count">{meter.items.length}</span>
          </div>
          <RiskMeterList items={meter.items} />
        </div>
      )}
    </div>
  );
}

function RiskMeterRow({
  item,
  open,
  onToggle,
}: {
  item: RiskMeterItem;
  open: boolean;
  onToggle: () => void;
}) {
  const isRisk = item.kind === "risk";
  return (
    <li className={`risk-meter-item ${isRisk ? "risk" : "positive"} ${open ? "open" : ""}`}>
      <button type="button" className="risk-meter-item-row" onClick={onToggle} aria-expanded={open}>
        <span className="risk-meter-item-ico" aria-hidden>
          {isRisk ? "\u26A0" : "\u2713"}
        </span>
        <span className="risk-meter-item-label">{item.label}</span>
        <span className={`risk-meter-item-weight ${isRisk ? "risk" : "positive"}`}>
          {isRisk ? "+" : "\u2212"}
          {item.weight}
        </span>
        {item.detail && (
          <span className="risk-meter-item-chevron" aria-hidden>
            {open ? "\u25B4" : "\u25BE"}
          </span>
        )}
      </button>
      {open && item.detail && <p className="risk-meter-item-detail">{item.detail}</p>}
    </li>
  );
}
